import * as XLSX from 'xlsx';
import { unzipSync } from 'fflate';

/** 在线预览默认截断：避免大表全量转 HTML / 撑爆浏览器内存 */
export const EXCEL_PREVIEW_MAX_ROWS = 2500;
export const EXCEL_PREVIEW_MAX_COLS = 100;
export const EXCEL_PREVIEW_MAX_SHEETS = 40;

export interface ExcelPreviewLimits {
  maxRows?: number;
  maxCols?: number;
  maxSheets?: number;
}

export function resolveExcelPreviewLimits(limits?: ExcelPreviewLimits) {
  return {
    maxRows: Math.max(1, limits?.maxRows ?? EXCEL_PREVIEW_MAX_ROWS),
    maxCols: Math.max(1, limits?.maxCols ?? EXCEL_PREVIEW_MAX_COLS),
    maxSheets: Math.max(1, limits?.maxSheets ?? EXCEL_PREVIEW_MAX_SHEETS),
  };
}

/** 将工作表 !ref 裁剪到指定行列上限（就地修改） */
export function clipWorksheetRange(
  ws: XLSX.WorkSheet,
  maxRows: number,
  maxCols: number,
): { truncated: boolean; endRow: number; endCol: number } {
  const ref = ws['!ref'];
  if (!ref) return { truncated: false, endRow: 0, endCol: 0 };
  try {
    const range = XLSX.utils.decode_range(ref);
    const endRow = Math.min(range.e.r, Math.max(0, maxRows - 1));
    const endCol = Math.min(range.e.c, Math.max(0, maxCols - 1));
    const truncated = endRow < range.e.r || endCol < range.e.c;
    range.e.r = endRow;
    range.e.c = endCol;
    if (range.s.r > range.e.r) range.s.r = range.e.r;
    if (range.s.c > range.e.c) range.s.c = range.e.c;
    ws['!ref'] = XLSX.utils.encode_range(range);
    return { truncated, endRow, endCol };
  } catch {
    return { truncated: false, endRow: 0, endCol: 0 };
  }
}

export function isExcelFileName(fileName: string): boolean {
  const lower = (fileName || '').toLowerCase();
  return lower.endsWith('.xlsx') || lower.endsWith('.xls');
}

export function isExcelMime(contentType: string): boolean {
  const ct = (contentType || '').toLowerCase();
  return ct.includes('spreadsheetml') || ct.includes('ms-excel');
}

function looksLikeXlsxZip(buffer: Uint8Array): boolean {
  if (buffer.length < 4 || buffer[0] !== 0x50 || buffer[1] !== 0x4b) return false;
  try {
    const files = unzipSync(buffer);
    return Object.keys(files).some((key) => key.startsWith('xl/'));
  } catch {
    return false;
  }
}

function looksLikeXlsOle(buffer: Uint8Array): boolean {
  return buffer.length > 4 && buffer[0] === 0xd0 && buffer[1] === 0xcf && buffer[2] === 0x11 && buffer[3] === 0xe0;
}

export function looksLikeExcelBuffer(buffer: Uint8Array, contentType = '', fileName = ''): boolean {
  // 优先按魔数判断：xlsx zip 含 xl/；勿把任意 OLE（含旧版 .doc）当成 Excel
  if (looksLikeXlsxZip(buffer)) return true;
  if (looksLikeXlsOle(buffer)) {
    const lower = (fileName || '').toLowerCase();
    // OLE 头同时被 .doc / .xls 使用，仅在扩展名或 MIME 明确为 Excel 时才当作表格
    return lower.endsWith('.xls') || lower.endsWith('.xlsx') || isExcelMime(contentType);
  }
  // 文件名声称是 Excel，但内容既不是 xlsx zip 也不是 OLE → 多半是误命名，交给其他解析器
  return false;
}

/** 尝试解析 Excel；失败返回 null（不抛错），便于回退到 Word/PDF 等 */
export function tryExtractExcelHtml(buffer: Uint8Array): string | null {
  try {
    const html = extractExcelHtml(buffer);
    return html || null;
  } catch {
    return null;
  }
}

function escapeHtml(text: string) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function escapeAttr(text: string) {
  return escapeHtml(text).replace(/`/g, '&#96;');
}

export const EXCEL_CELL_REF_PATTERN = /^[A-Z]{1,4}\d{1,7}$/i;

/** 从段落标签或单元格 id 解析 Excel 坐标（如 D11） */
export function parseExcelCellRef(label?: string | null): string | null {
  const raw = (label || '').trim();
  if (!raw) return null;
  if (EXCEL_CELL_REF_PATTERN.test(raw)) return raw.toUpperCase();
  const tail = raw.match(/(?:^|[·\s!])([A-Z]{1,4}\d{1,7})$/i);
  return tail ? tail[1].toUpperCase() : null;
}

export function columnIndexToLetter(colIndex: number): string {
  let n = colIndex + 1;
  let letters = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    letters = String.fromCharCode(65 + rem) + letters;
    n = Math.floor((n - 1) / 26);
  }
  return letters;
}

/** 由表格内 0-based 行列索引生成 Excel 坐标（如 row=10,col=3 → D11） */
export function encodeExcelCellRef(colIndex: number, rowIndex: number): string {
  return `${columnIndexToLetter(colIndex)}${rowIndex + 1}`;
}

function isExcelTableCell(cell: Element): boolean {
  return !!(
    cell.closest('.excel-workbook, .excel-sheet')
    || cell.classList.contains('excel-cell')
    || /^sjs-[A-Z]+\d+$/i.test(cell.id || '')
  );
}

function readExcelCellRefFromCellElement(cell: HTMLTableCellElement): string | null {
  const dataRef = (cell.getAttribute('data-cell-ref') || '').trim();
  if (dataRef && EXCEL_CELL_REF_PATTERN.test(dataRef)) return dataRef.toUpperCase();
  const idMatch = (cell.id || '').match(/^sjs-([A-Z]+\d+)$/i);
  if (idMatch) return idMatch[1].toUpperCase();
  if (!isExcelTableCell(cell)) return null;
  const row = cell.parentElement;
  const table = cell.closest('table');
  if (!row || row.tagName !== 'TR' || !table) return null;
  const rowIndex = Array.from(table.rows).indexOf(row as HTMLTableRowElement);
  const colIndex = cell.cellIndex;
  if (rowIndex < 0 || colIndex < 0) return null;
  return encodeExcelCellRef(colIndex, rowIndex);
}

export function getExcelCellRefFromElement(el: Element | null): string | null {
  if (!el) return null;
  const cell = el.closest('td, th') as HTMLTableCellElement | null;
  if (!cell) return null;
  return readExcelCellRefFromCellElement(cell);
}

function nodeToElement(node: Node | null | undefined): Element | null {
  if (!node) return null;
  return node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
}

export function isExcelHeaderCell(cell: HTMLTableCellElement | null): boolean {
  if (!cell) return false;
  if (cell.tagName === 'TH') return true;
  if (cell.classList.contains('excel-cell--head')) return true;
  const row = cell.parentElement as HTMLTableRowElement | null;
  const table = cell.closest('table');
  if (!row || !table || row.tagName !== 'TR') return false;
  return Array.from(table.rows).indexOf(row) === 0;
}

function excelCellElementFromRef(root: ParentNode | null | undefined, ref: string | null): HTMLTableCellElement | null {
  if (!ref) return null;
  const el = findExcelCellElement(root || document, ref);
  return el?.closest('td, th') as HTMLTableCellElement | null;
}

/** 跨单元格选区时 Range 会按文档顺序归一化，start 可能落在表头；优先取选区起点（anchor）且非表头的单元格 */
export function getExcelCellRefFromRange(
  range: Range | null | undefined,
  selection?: Selection | null,
): string | null {
  if (!range) return null;
  const sel = selection ?? (typeof window !== 'undefined' ? window.getSelection() : null);

  const orderedRefs = [
    getExcelCellRefFromElement(nodeToElement(sel?.anchorNode)),
    getExcelCellRefFromElement(nodeToElement(sel?.focusNode)),
    getExcelCellRefFromElement(nodeToElement(range.startContainer)),
    getExcelCellRefFromElement(nodeToElement(range.endContainer)),
  ].filter(Boolean) as string[];

  const unique = [...new Set(orderedRefs)];
  if (!unique.length) return null;
  if (unique.length === 1) return unique[0];

  const root = nodeToElement(range.commonAncestorContainer)?.closest('.excel-workbook, .excel-sheet, table')
    || document;
  for (const ref of orderedRefs) {
    const cell = excelCellElementFromRef(root, ref);
    if (cell && !isExcelHeaderCell(cell)) return ref;
  }
  return orderedRefs[0];
}

export function isMultiCellExcelRange(range: Range | null | undefined): boolean {
  if (!range) return false;
  const startRef = getExcelCellRefFromElement(nodeToElement(range.startContainer));
  const endRef = getExcelCellRefFromElement(nodeToElement(range.endContainer));
  return !!(startRef && endRef && startRef !== endRef);
}

export function findExcelCellElement(root: ParentNode, cellRef: string): HTMLElement | null {
  const ref = (cellRef || '').trim().toUpperCase();
  if (!ref || !EXCEL_CELL_REF_PATTERN.test(ref)) return null;
  const byData = root.querySelector(`[data-cell-ref="${ref}"]`) as HTMLElement | null;
  if (byData) return byData;
  return root.querySelector(`#sjs-${ref}`) as HTMLElement | null;
}

function annotateExcelCellRefs(tableHtml: string) {
  return tableHtml.replace(/<(td|th)([^>]*)\bid="sjs-([A-Z]+\d+)"([^>]*)>/gi, (full, tag, pre, ref, post) => {
    if (/data-cell-ref=/i.test(full)) return full;
    const upperRef = ref.toUpperCase();
    return `<${tag}${pre}id="sjs-${ref}" data-cell-ref="${upperRef}"${post}>`;
  });
}

/** 按表格 DOM 行列顺序，为所有 Excel 单元格写入稳定的 data-cell-ref */
export function stampExcelCellRefsOnHtml(html: string): string {
  if (!html?.trim()) return html;
  try {
    const doc = new DOMParser().parseFromString(`<div id="aipd-excel-stamp-root">${html}</div>`, 'text/html');
    const root = doc.getElementById('aipd-excel-stamp-root');
    if (!root) return html;
    const tables = root.querySelectorAll('.excel-workbook table, .excel-sheet table, table.excel-table, table.docx-table');
    tables.forEach((table) => {
      Array.from((table as HTMLTableElement).rows).forEach((row, rowIndex) => {
        Array.from(row.cells).forEach((cell, colIndex) => {
          const ref = encodeExcelCellRef(colIndex, rowIndex);
          if (!cell.getAttribute('data-cell-ref')) {
            cell.setAttribute('data-cell-ref', ref);
          }
          if (!cell.id) {
            cell.id = `sjs-${ref}`;
          }
          if (!cell.classList.contains('excel-cell')) {
            cell.classList.add('excel-cell');
          }
        });
      });
    });
    return root.innerHTML;
  } catch {
    return html;
  }
}

function normalizeSheetTableHtml(tableHtml: string) {
  return annotateExcelCellRefs(
    tableHtml
      .replace(/<table\b/i, '<table class="excel-table docx-table"')
      .replace(/<td\b/gi, '<td class="excel-cell"')
      .replace(/<th\b/gi, '<th class="excel-cell excel-cell--head"'),
  );
}

/** 将 Excel 二进制解析为可在文档编辑器中预览/编辑的 HTML（按预览上限截断） */
export function extractExcelHtml(buffer: Uint8Array, limits?: ExcelPreviewLimits): string {
  const { maxRows, maxCols, maxSheets } = resolveExcelPreviewLimits(limits);
  const wb = XLSX.read(buffer, {
    type: 'array',
    cellDates: true,
    cellNF: false,
    cellText: false,
    cellStyles: false,
    sheetRows: maxRows,
  });
  const sheetNames = wb.SheetNames.filter(Boolean).slice(0, maxSheets);
  const hiddenFlags = (wb as XLSX.WorkBook & { Workbook?: { Sheets?: Array<{ Hidden?: number }> } })
    .Workbook?.Sheets || [];
  if (!sheetNames.length) {
    return '<div class="doc-empty-state">Excel 文件中没有可展示的工作表</div>';
  }

  let truncated = wb.SheetNames.length > maxSheets;
  const parts: string[] = ['<div class="excel-workbook">'];
  sheetNames.forEach((sheetName) => {
    const ws = wb.Sheets[sheetName];
    if (!ws) return;
    const clip = clipWorksheetRange(ws, maxRows, maxCols);
    if (clip.truncated) truncated = true;
    const tableHtml = XLSX.utils.sheet_to_html(ws, { editable: false, header: '', footer: '' });
    if (!tableHtml || !/<table/i.test(tableHtml)) return;
    const originalIndex = wb.SheetNames.indexOf(sheetName);
    const hidden = hiddenFlags[originalIndex]?.Hidden;
    const state = hidden === 2 ? 'veryHidden' : hidden === 1 ? 'hidden' : 'visible';
    parts.push(`<section class="excel-sheet" data-sheet-name="${escapeAttr(sheetName)}" data-sheet-state="${state}">`);
    parts.push(`<h2 class="excel-sheet-title">${escapeHtml(sheetName)}</h2>`);
    parts.push(`<div class="excel-sheet-scroll">${normalizeSheetTableHtml(tableHtml)}</div>`);
    parts.push('</section>');
  });
  parts.push('</div>');
  if (truncated) {
    parts.unshift(
      `<div class="excel-preview-truncated">在线预览已截断至前 ${maxSheets} 个工作表、每表 ${maxRows} 行 × ${maxCols} 列；完整内容请导出后用 Excel 打开。</div>`,
    );
  }
  const html = stampExcelCellRefsOnHtml(parts.join('\n'));
  return html.includes('<table') ? html : '<div class="doc-empty-state">Excel 工作表为空</div>';
}

export function normalizeExcelHtml(html: string) {
  return stampExcelCellRefsOnHtml(
    annotateExcelCellRefs(
      html
        .replace(/<table(?![^>]*class=)/gi, '<table class="excel-table docx-table"')
        .replace(/<td(?![^>]*class=)/gi, '<td class="excel-cell"')
        .replace(/<th(?![^>]*class=)/gi, '<th class="excel-cell excel-cell--head"'),
    ),
  );
}

function sanitizeSheetName(name: string, index: number): string {
  const trimmed = (name || '').trim() || `Sheet${index + 1}`;
  return trimmed.replace(/[\\/?*[\]:]/g, '_').slice(0, 31);
}

/** 将编辑器中的 HTML 表格还原为 Excel 文件（用于导出已编辑内容） */
export function exportExcelBlobFromHtml(html: string, preferXlsx = true): Blob | null {
  if (!html?.trim()) return null;
  try {
    const doc = new DOMParser().parseFromString(`<div id="aipd-excel-root">${html}</div>`, 'text/html');
    const root = doc.getElementById('aipd-excel-root');
    if (!root) return null;

    const wb = XLSX.utils.book_new();
    const sections = Array.from(root.querySelectorAll('.excel-sheet[data-sheet-name]'));
    if (sections.length) {
      sections.forEach((section, index) => {
        const table = section.querySelector('table');
        if (!table) return;
        const name = sanitizeSheetName(section.getAttribute('data-sheet-name') || '', index);
        const ws = XLSX.utils.table_to_sheet(table);
        XLSX.utils.book_append_sheet(wb, ws, name);
      });
    } else {
      const tables = Array.from(root.querySelectorAll('table'));
      if (!tables.length) return null;
      tables.forEach((table, index) => {
        const ws = XLSX.utils.table_to_sheet(table);
        XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(`Sheet${index + 1}`, index));
      });
    }

    if (!wb.SheetNames.length) return null;
    const bookType = preferXlsx ? 'xlsx' : 'xls';
    const bytes = XLSX.write(wb, { bookType, type: 'array' }) as Uint8Array;
    const mime = preferXlsx
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'application/vnd.ms-excel';
    return new Blob([bytes], { type: mime });
  } catch {
    return null;
  }
}
